import React, { useState } from 'react';
import { Redirect } from 'react-router';
import { t } from 'ttag';
import * as Yup from 'yup';
import { Services as sdkServices } from 'coolremote-sdk';

// Import unified components
import {
  AuthLayout,
  AuthForm,
  AuthField,
  AuthButton,
  AuthLink,
  AuthMessage
} from '../UnifiedComponents';

// Import theme service
import themeService from '../../services/themeService';

// Import existing components
import assetsService from '../../services/assetsService';

// Validation schema
const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
});

interface ForgotPasswordProps {
  theme?: 'light' | 'dark';
}

const UnifiedForgotPassword: React.FC<ForgotPasswordProps> = ({ theme = 'light' }) => {
  // Component state
  const [shouldNavigateToLogin, setShouldNavigateToLogin] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form initial values
  const initialFormValues = {
    email: '',
  };

  /**
   * Handles navigation back to login page
   */
  const handleNavigateToLogin = () => {
    setShouldNavigateToLogin(true);
  };

  /**
   * Handles password reset form submission
   * @param formData - Form data containing email
   */
  const handlePasswordResetSubmission = async (formData: { email: string }) => {
    if (!formData.email) return;

    setIsSubmittingForm(true);
    setFormError(null);
    setSuccessMessage(null);

    try {
      // Call the SDK directly like the old implementation
      const payload = { email: formData.email, app: "commercial" };
      const data = await sdkServices.forgotPassword(payload);

      if (data) {
        // Show success message
        setSuccessMessage(t`Password reset instructions sent successfully! Please check your email.`);
        // Clear form
        formData.email = '';
      } else {
        setFormError("Email not found");
      }

    } catch (error: any) {
      setFormError(error.message || 'An error occurred while processing your request');
    } finally {
      setIsSubmittingForm(false);
    }
  };

  // Handle navigation redirects
  if (shouldNavigateToLogin) {
    return <Redirect to='/' />;
  }

  // Get theme configuration from service
  const themeConfig = themeService.getThemeConfig(theme);

  return (
    <AuthLayout
      theme={theme}
      title={t`Forgot Password`}
      subtitle={t`Enter your email address and we'll send you a link to reset your password`}
      logo={assetsService.getResourcePath('logo.png')}
      onLogoError={() => {
        // Handle logo error silently
      }}
    >
      {/* Success Message */}
      {successMessage && (
        <AuthMessage
          theme={theme}
          type="success"
          message={successMessage}
        />
      )}

      {/* Forgot Password Form */}
      <AuthForm
        theme={theme}
        initialValues={initialFormValues}
        validationSchema={ForgotPasswordSchema}
        onSubmit={handlePasswordResetSubmission}
        submitButtonText={isSubmittingForm ? t`Sending...` : t`Reset Password`}
        isSubmitting={isSubmittingForm}
      >
        {/* Email Field */}
        <AuthField
          theme={theme}
          label={t`Email Address`}
          name="email"
          type="email"
          required
        />

        {/* Error Display */}
        {formError && (
          <AuthMessage
            theme={theme}
            type="error"
            message={formError}
          />
        )}
      </AuthForm>

      {/* Back to Login Link */}
      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        <AuthLink
          theme={theme}
          onClick={handleNavigateToLogin}
          ariaLabel={t`Navigate back to login page`}
        >
          {t`Back to Login`}
        </AuthLink>
      </div>

      {/* Support Contact Information */}
      <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px', color: themeConfig.colors.text }}>
        <p>
          {t`Forgot your username or email?`}
          <br />
          {t`Please contact `}
          <a
            href="mailto:support@coolremote.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: themeConfig.colors.text, textDecoration: 'underline' }}
          >
            support@coolremote.com
          </a>
        </p>
      </div>
    </AuthLayout>
  );
};

export default UnifiedForgotPassword;
